<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard>
    <template #subheader>
      <ReviewsProductReviewDetailsSubheader
        v-if="surveyResponse"
        :product="product"
        :survey-response="surveyResponse"
        @send-reply="openReplyDialog"
      />
    </template>
    <div v-if="surveyResponse">
      <ReviewsProductReviewDetails
        :product="product"
        :survey-response="surveyResponse"
        :reminder-disabled="surveyResponse.reminderDisabled"
        :has-old-review="hasOldReview"
        @send-reminder="openReminderDialog"
        @edit-reply="openReplyDialog"
        @delete-reply="handleDeleteReviewReply"
      />
      <ReviewsProductReviewEmbed v-if="isPublished && surveyResponse.review?.id" :product="product" :survey-response="surveyResponse" />
      <SendReminderDialog
        ref="sendReminderModal"
        :product="product"
        :survey-response="surveyResponse"
        :has-old-review="hasOldReview"
        @closed="refetch"
      />
      <ReviewReplyDialog
        v-if="surveyResponse.review?.id"
        ref="reviewReplyModal"
        subject="product"
        :review-id="surveyResponse.review.id"
        :review-reply="surveyResponse.review.reviewReply"
        :entity-title="product.title"
        :detail-page-url="detailPageUrl"
        :reviewer-first-name="reviewerFirstName"
        @closed="refetch"
        @submitted="onReplySubmitted"
      />
      <ReviewReplyConfirmModal
        :show-confirmation-modal="showDeleteConfirmationModal"
        @canceled="onDeleteReviewReply(false)"
        @confirmed="onDeleteReviewReply(true)"
      />
    </div>
  </ContentCard>
</template>
<script setup lang="ts">
import { reviewsPaths } from '@ramp106/omrjs-routing'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { differenceInYears } from 'date-fns'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import ReviewReplyConfirmModal from '@/components/ReviewsCommon/ReviewReply/ReviewReplyConfirmModal.vue'
import ReviewReplyDialog from '@/components/ReviewsCommon/ReviewReply/ReviewReplyDialog.vue'
import SendReminderDialog from '@/components/ReviewsManagement/Reviews/SendReminderDialog.vue'
import { buildBreadcrumb } from '@/composables/breadcrumbs/buildBreadcrumb'
import { SurveyResponseState } from '@/composables/useSurveyResponseState'
import { type ProductData, useDeleteReviewReplyMutation, useReviewsProductSurveyResponseDetailsQuery } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import ContentCard from '@/ui/ContentCard.vue'

import ReviewsProductReviewDetails from './ReviewsProductReviewDetails.vue'
import ReviewsProductReviewDetailsSubheader from './ReviewsProductReviewDetailsSubheader.vue'
import ReviewsProductReviewEmbed from './ReviewsProductReviewEmbed.vue'

const props = defineProps<{
  product: ProductData
}>()

const route = useRoute()
const { t, locale } = useI18n()

const sendReminderModal = ref<InstanceType<typeof SendReminderDialog> | null>(null)
const reviewReplyModal = ref<InstanceType<typeof SendReminderDialog> | null>(null)

const showDeleteConfirmationModal = ref(false)

const variables = computed(() => ({ id: route.params.surveyResponseId as string }))
const { result, refetch } = useReviewsProductSurveyResponseDetailsQuery(variables, { clientId: 'reviews' })

const { mutate: mutateDeleteReviewReply } = useDeleteReviewReplyMutation({ clientId: 'reviews' })

const surveyResponse = computed(() => result.value?.manage?.surveyResponse)

const isPublished = computed(() => surveyResponse.value?.aasmState === SurveyResponseState.Published)

const reviewerFirstName = computed(() => surveyResponse.value?.reviewerFirstName || undefined)

const detailPageUrl = computed(() => reviewsPaths.product({ locale: locale.value, slug: props.product.slug }))

const breadcrumbs = computed(() => {
  const reviewsRoot = buildBreadcrumb(route, { name: ReviewsRouteName.ReviewsProducts }, t('breadcrumbs.reviews.root'))
  const reviewsProductViewReviews = buildBreadcrumb(
    route,
    {
      name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns,
      params: { productId: props.product.id },
    },
    t('breadcrumbs.reviews.product.view.reviews', { productName: props.product.title }),
  )
  const reviewDetails = { text: t('breadcrumbs.reviews.product.view.reviewDetails', { authorName: reviewerFirstName.value || 'Anonym' }) }

  return [reviewsRoot, reviewsProductViewReviews, reviewDetails]
})

const reviewReplyPayload = computed(() =>
  surveyResponse.value?.review?.id
    ? { surface: 'editor' as const, entity_type: 'tool' as const, entity_id: props.product.id, review_id: surveyResponse.value.review.id }
    : undefined,
)

const hasOldReview = computed(() => {
  if (surveyResponse.value?.review?.publishedAt) {
    return differenceInYears(new Date(), new Date(surveyResponse.value.review.publishedAt.toString())) >= 1
  }
  return false
})

function openReminderDialog() {
  sendReminderModal.value?.open()
}

function openReplyDialog() {
  if (reviewReplyPayload.value) {
    track('response_start', reviewReplyPayload.value)
  }
  reviewReplyModal.value?.open()
}

function onReplySubmitted() {
  if (reviewReplyPayload.value) {
    track('review_respond', reviewReplyPayload.value)
  }
}

function handleDeleteReviewReply() {
  showDeleteConfirmationModal.value = true
}

async function onDeleteReviewReply(confirmed: boolean) {
  if (surveyResponse.value?.review?.reviewReply?.id && confirmed) {
    try {
      await mutateDeleteReviewReply({ id: surveyResponse.value.review.reviewReply.id })
      refetch()
      showNotification(t('Reviews.reviewReply.delete.success'), 'success', 10000)
    } catch (_e) {
      showNotification(t('Reviews.reviewReply.delete.error'), 'success', 10000)
    }
  }
  showDeleteConfirmationModal.value = false
}
</script>
